<template>
  <div class="crypto-currency-wallets-select">
    <div class="crypto-currency-wallets-select__header">
      <div class="crypto-currency-wallets-select__close">
        <IconClose @click="emit('close')" />
      </div>
      <div v-if="props.loading"></div>
      <ExchangeWalletTabs
        v-else
        v-model="walletType"
        :isPaybisWalletAvailable="isPaybisWalletAvailable"
      />
    </div>

    <div
      v-if="props.loading"
      class="crypto-currency-wallets-select__loading"
    >
      <ExchangeLoading />
    </div>

    <template v-else>

      <div class="crypto-currency-wallets-select__sub-header">
        <div class="crypto-currency-wallets-select__search">
          <FilterInput v-model="searchCryptoCurrency" />
        </div>
        <div
          v-if="walletType === WalletTypes.EXTERNAL"
          class="crypto-currency-wallets-select__sub-heading"
        >
          {{ i18n.t(`exchange-form.drop-down.crypto-title.${ props.flow }`) }}
        </div>
      </div>

      <PbScroll>
        <div class="crypto-currency-wallets-select__list">
          <template v-if="walletType === WalletTypes.PAYBIS">
            <template v-if="TokenService.hasToken()">
              <WalletItem
                v-for="(wallet, index) in availableWallets"
                :key="index"
                :wallet="wallet"
                :active="props.value === wallet"
                @click.native="selectHandler(wallet)"
              />
            </template>
            <ExchangeWalletGuestLayout v-else />
          </template>
          <template v-else-if="walletType === WalletTypes.EXTERNAL">
            <CryptoCurrency
              class="crypto-currency-item-override"
              v-for="(option, index) in items"
              :key="index"
              :selected="props.value instanceof CryptoOptionModel && props.value.currency === option.currency"
              :currency="option.currency"
              :icon="option.icon"
              :label="option.name"
              @click.native="selectHandler(option)"
            />
          </template>
        </div>
      </PbScroll>
    </template>
  </div>
</template>

<script lang="ts" setup>
import TokenService from '@paybis/frontend-common-lib/src/services/token.js';
import { computed, onUnmounted, ref, watch } from 'vue';

import CryptoCurrency from '@/components/ExchangeFormV2/CryptoCurrency.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import ExchangeWalletGuestLayout from '@/components/ExchangeFormV2/ExchangeWalletGuestLayout.vue';
import ExchangeWalletTabs from '@/components/ExchangeFormV2/ExchangeWalletTabs.vue';
import FilterInput from '@/components/ExchangeFormV2/FilterInput.vue';
import PbScroll from '@/components/ExchangeFormV2/PbScroll.vue';
import WalletItem from '@/components/ExchangeFormV2/WalletItem.vue';
import { i18n } from '@/i18n';
import { BaseOptionModel, CryptoOptionModel, WalletOptionModel } from '@/models';
import { Flow, WalletTypes } from '@/types';

import IconClose from '../../../assets/images/exchange-form/close.svg';

const props = withDefaults(defineProps<{
  value: BaseOptionModel
  flow: Flow
  items: CryptoOptionModel[]
  wallets: WalletOptionModel[]
  loading: boolean
  flowSwitchDisabled?: boolean
  allowWebWallets: boolean,
}>(), {
  allowWebWallets: true,
});

const emit = defineEmits<{
  (event: 'close'): void,
  (event: 'input', option: CryptoOptionModel | WalletOptionModel): void,
}>();

const searchCryptoCurrency = ref('');
onUnmounted(() => searchCryptoCurrency.value = '');

const items = computed(() => {
  if (searchCryptoCurrency.value.length) {
    const search = searchCryptoCurrency.value.toLowerCase();
    return props.items.filter(item => {
      return item.currency.toLowerCase().includes(search) || item.name.toLowerCase().includes(search);
    });
  }

  return props.items;
});
const availableWallets = computed(() => {
  if (searchCryptoCurrency.value.length > 0) {
    const search = searchCryptoCurrency.value.toLowerCase();
    return props.wallets.filter(({ currency, name, assetName }) => {
      return currency.toLowerCase().includes(search) || name.toLowerCase().includes(search) || assetName.toLowerCase().includes(search);
    });
  }

  return props.wallets;
});
const isPaybisWalletAvailable = computed(() => props.allowWebWallets && (availableWallets.value.length > 0 || !TokenService.hasToken()));

const walletType = ref<WalletTypes>(WalletTypes.EXTERNAL);

watch(walletType, () => searchCryptoCurrency.value = '');
watch(() => props.loading, () => walletType.value = props.value instanceof CryptoOptionModel || !props.allowWebWallets || availableWallets.value.length === 0 ? WalletTypes.EXTERNAL : WalletTypes.PAYBIS);

const selectHandler = (option: CryptoOptionModel | WalletOptionModel) => {
  emit('input', option);
  emit('close');
};
</script>

<style lang="scss" scoped>
@import "@scss-vars";

.crypto-currency-wallets-select {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: flex;
    flex-direction: column;
    align-items: end;
    padding: 16px 16px 6px;

    @media screen and (max-width: $screen-lg-min) {
      padding: 20px 24px 8px;
    }
  }

  &__sub-header {
    padding: 6px 16px;

    @media screen and (max-width: $screen-lg-min) {
      padding: 8px 24px;
    }
  }

  &__search {
    grid-area: search;
  }

  &__sub-heading {
    font-family: $font-bold;
    font-size: rem(14);
    color: $black-50;
    margin: 16px 0 8px;
  }

  &__close {
    display: none;
    width: rem(24);
    height: rem(24);

    & svg {
      width: rem(24);
      height: rem(24);
    }

    @media screen and (max-width: $screen-lg-min) {
      display: block;
    }
  }

  &__list {
    display: grid;
    grid-gap: 2px;
    margin: 0 8px;
    padding-bottom: 8px;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.crypto-currency-item-override {
  padding: 8px 5px;
}
</style>
